import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, RefreshCw, IndianRupee, Car, Wrench, Shield, MapPin, ExternalLink, CheckCircle2, XCircle, Globe } from 'lucide-react';
import { AdminService } from '../../services/adminService';
import { marketingAdmin } from '../../services/marketingAdminService';
import { notifyAppEnquiryWhatsApp } from '../../services/appEnquiryWhatsAppService';

const TABS = [
  { id: 'car_loan', label: 'Car Loan', icon: IndianRupee },
  { id: 'car_service', label: 'Car Service', icon: Wrench },
  { id: 'rsa', label: 'RSA', icon: Shield },
  { id: 'website', label: 'Website', icon: Globe },
];

const statusColors = {
  submitted: 'bg-blue-100 text-blue-700',
  pending: 'bg-yellow-100 text-yellow-700',
  pending_activation: 'bg-amber-100 text-amber-800',
  active: 'bg-green-100 text-green-700',
  paid: 'bg-emerald-100 text-emerald-800',
  confirmed: 'bg-green-100 text-green-700',
  requested: 'bg-orange-100 text-orange-700',
  registered: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-700',
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-800',
  converted: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const fmtDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const parseGarageNotes = (notes) => {
  if (!notes) return {};
  try {
    return typeof notes === 'string' ? JSON.parse(notes) : notes;
  } catch {
    return {};
  }
};

const hasGps = (row) => {
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

const fmtGps = (row) => {
  if (!hasGps(row)) return null;
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const mapsUrl = (row) => {
  if (row.location_link) return row.location_link;
  if (!hasGps(row)) return null;
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

const RsaLocationCell = ({ row }) => {
  const gps = fmtGps(row);
  const url = mapsUrl(row);

  if (!gps && !row.pickup_address) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <div className="space-y-1.5 max-w-xs">
      {gps ? (
        <>
          <div className="inline-flex items-start gap-1.5 text-sm font-mono text-gray-900">
            <MapPin size={14} className="text-red-500 mt-0.5 shrink-0" />
            <span>{gps}</span>
          </div>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              Open in Google Maps
              <ExternalLink size={12} />
            </a>
          ) : null}
        </>
      ) : null}
      {row.pickup_address ? (
        <p className={`text-xs text-gray-500 ${gps ? 'line-clamp-2' : ''}`} title={row.pickup_address}>
          {gps ? `Address: ${row.pickup_address}` : row.pickup_address}
        </p>
      ) : null}
      {gps ? (
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(gps)}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Copy coordinates
        </button>
      ) : null}
    </div>
  );
};

const fmtMembershipCard = (card) => {
  if (!card) return null;
  const digits = String(card).replace(/\D/g, '');
  if (digits.length !== 16) return card;
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12, 16)}`;
};

const fmtVehicleTitle = (row) => {
  const parts = [row.vehicle_brand, row.vehicle_model].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
};

const CustomerCell = ({ row }) => (
  <div className="space-y-1 max-w-[180px]">
    <div className="text-sm font-medium text-gray-900">{row.customer_name || '—'}</div>
    <div className="text-sm text-gray-600">{row.customer_phone || '—'}</div>
    {row.customer_email ? <div className="text-xs text-gray-500 break-all">{row.customer_email}</div> : null}
    {row.customer_city || row.customer_address ? (
      <div className="text-xs text-gray-500">
        {[row.customer_city, row.customer_pincode].filter(Boolean).join(' · ')}
      </div>
    ) : null}
  </div>
);

const GarageVehicleCell = ({ row }) => {
  const title = [row.vehicle_brand, row.vehicle_model].filter(Boolean).join(' ');
  if (!row.vehicle_registration && !title) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  return (
    <div className="space-y-1 max-w-[160px]">
      <div className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
        {row.vehicle_registration || '—'}
      </div>
      {title ? <div className="text-xs text-gray-600">{title}</div> : null}
      {row.vehicle_fuel_type ? (
        <div className="text-xs text-gray-500 capitalize">{row.vehicle_fuel_type}</div>
      ) : null}
    </div>
  );
};

const GarageServiceCell = ({ row }) => {
  const category = String(row.service_name || '').replace(/-/g, ' ');
  const points = Number(row.package_points || 0);
  return (
    <div className="space-y-1.5 max-w-[240px]">
      <div className="capitalize font-semibold text-gray-900">{category || '—'}</div>
      {row.package_name ? (
        <div className="text-sm text-gray-800 leading-snug">{row.package_name}</div>
      ) : row.package_key ? (
        <div className="text-xs text-gray-400 font-mono truncate">{row.package_key}</div>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {points > 0 ? (
          <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-semibold">
            {points} points
          </span>
        ) : null}
        {row.package_oil_type ? (
          <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold">
            {row.package_oil_type}
          </span>
        ) : null}
      </div>
      {row.package_checklist_title ? (
        <div className="text-xs text-gray-500">{row.package_checklist_title}</div>
      ) : null}
    </div>
  );
};

const RsaVehicleCell = ({ row }) => {
  const title = fmtVehicleTitle(row);
  if (!row.vehicle_registration && !title) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  return (
    <div className="space-y-1 max-w-[160px]">
      <div className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
        {row.vehicle_registration || '—'}
      </div>
      {title ? <div className="text-xs text-gray-600">{title}</div> : null}
      {row.vehicle_fuel_type ? (
        <div className="text-xs text-gray-500 capitalize">{row.vehicle_fuel_type}</div>
      ) : null}
    </div>
  );
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = statusColors[key] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded ${cls}`}>
      {value || '—'}
    </span>
  );
};

const RsaMembershipCell = ({ row }) => {
  if (!row.membership_plan_name && !row.membership_card_number) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const used = Number(row.membership_services_used ?? 0);
  const limit = Number(row.membership_services_limit ?? 2);
  const card = fmtMembershipCard(row.membership_card_number);

  return (
    <div className="space-y-1.5 max-w-[180px]">
      <div className="text-sm font-semibold text-gray-900">{row.membership_plan_name || 'RSA Plan'}</div>
      {card ? (
        <div className="text-xs font-mono text-gray-700">{card}</div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {row.membership_status ? <StatusBadge value={row.membership_status} /> : null}
        {row.membership_payment_status && row.membership_payment_status !== row.membership_status ? (
          <StatusBadge value={row.membership_payment_status} />
        ) : null}
      </div>
      {row.membership_started_at || row.membership_expires_at ? (
        <div className="text-xs text-gray-500">
          {row.membership_started_at ? fmtDate(row.membership_started_at).split(',')[0] : '—'}
          {' → '}
          {row.membership_expires_at ? fmtDate(row.membership_expires_at).split(',')[0] : '—'}
        </div>
      ) : null}
      <div className="text-xs text-gray-600">
        Services: {used}/{limit} used
      </div>
      {row.membership_amount_paid != null ? (
        <div className="text-xs text-gray-500">
          Paid: ₹{Number(row.membership_amount_paid).toLocaleString('en-IN')}
        </div>
      ) : null}
    </div>
  );
};

const isRsaClosed = (row) => {
  const req = String(row.request_status || '').toLowerCase();
  const comp = String(row.complaint_status || '').toLowerCase();
  return ['completed', 'cancelled', 'closed'].includes(req) || ['completed', 'cancelled', 'closed'].includes(comp);
};

const isSimpleEnquiryClosed = (row) => {
  const status = String(row.status || '').toLowerCase();
  return ['completed', 'cancelled', 'rejected', 'closed'].includes(status);
};

const EnquiryActionButtons = ({ row, closed, updating, onComplete, onCancel }) => {
  if (closed) {
    return <span className="text-xs text-gray-400">Closed</span>;
  }
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={onComplete}
        disabled={!!updating}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
      >
        <CheckCircle2 size={14} />
        {updating === 'complete' ? '...' : 'Complete'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={!!updating}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
      >
        <XCircle size={14} />
        {updating === 'cancel' ? '...' : 'Cancel'}
      </button>
    </div>
  );
};

const AppEnquiries = () => {
  const [activeTab, setActiveTab] = useState('car_loan');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [updating, setUpdating] = useState({});
  const notifiedIdsRef = useRef(new Set());

  // Realtime notify lives in AdminLayout (any admin page). Tab open only backfills missed alerts.

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['adminAppEnquiries', activeTab, search],
    queryFn: async () => {
      if (activeTab === 'website') {
        let query = marketingAdmin
          .from('website_leads')
          .select('*', { count: 'exact' })
          .order('created_at', { ascending: false })
          .limit(100);

        if (search) {
          const term = search.replace(/[%_]/g, '');
          query = query.or(`name.ilike.%${term}%,phone.ilike.%${term}%,message.ilike.%${term}%`);
        }

        const { data: rows, error, count } = await query;
        if (error) throw error;
        return { success: true, rows: rows || [], total: count || 0 };
      }

      return AdminService.getAppEnquiries(activeTab, { search, limit: 100 });
    },
  });

  const rows = data?.rows || [];
  const total = data?.total ?? rows.length;

  // Backfill only: if DB trigger missed a recent enquiry, edge dedup prevents double WhatsApp
  useEffect(() => {
    if (activeTab === 'website' || !rows.length) return;
    const cutoff = Date.now() - 6 * 60 * 60 * 1000;
    rows.forEach((row) => {
      const closed = activeTab === 'rsa'
        ? isRsaClosed(row)
        : isSimpleEnquiryClosed(row);
      if (closed || !row.id) return;
      const createdAt = row.created_at || row.requested_at || row.complaint_registered_at;
      if (createdAt && new Date(createdAt).getTime() < cutoff) return;
      const key = `${activeTab}:${row.id}`;
      if (notifiedIdsRef.current.has(key)) return;
      notifiedIdsRef.current.add(key);
      notifyAppEnquiryWhatsApp(activeTab, row.id, null);
    });
  }, [rows, activeTab]);

  const tabCounts = useMemo(() => ({
    car_loan: activeTab === 'car_loan' ? total : null,
    car_service: activeTab === 'car_service' ? total : null,
    rsa: activeTab === 'rsa' ? total : null,
    website: activeTab === 'website' ? total : null,
  }), [activeTab, total]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleEnquiryAction = async (type, row, action) => {
    const label = action === 'complete' ? 'Complete' : 'Cancel';
    const typeLabel =
      type === 'car_loan' ? 'car loan enquiry' : type === 'car_service' ? 'car service booking' : 'RSA request';
    const message =
      action === 'complete'
        ? `Mark this ${typeLabel} for ${row.customer_name || 'customer'} as completed?`
        : type === 'rsa'
          ? `Cancel this ${typeLabel} for ${row.customer_name || 'customer'}? The service slot will be restored.`
          : `Cancel this ${typeLabel} for ${row.customer_name || 'customer'}?`;
    if (!window.confirm(message)) return;

    const note = window.prompt(`${label} note (optional):`, '') || null;

    setUpdating((prev) => ({ ...prev, [row.id]: action }));
    try {
      let res;
      if (type === 'car_loan') {
        res = await AdminService.updateCarLoanEnquiryStatus(row.id, action, note);
      } else if (type === 'car_service') {
        res = await AdminService.updateCarServiceEnquiryStatus(row.id, action, note);
      } else {
        res = await AdminService.updateRsaEnquiryStatus(row.id, action, note);
      }
      if (!res.success) {
        alert(res.error || `Failed to ${label.toLowerCase()} enquiry`);
        return;
      }
      await refetch();
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  };

  const handleWebsiteStatus = async (row, nextStatus) => {
    if (!row?.id || row.status === nextStatus) return;
    setUpdating((prev) => ({ ...prev, [row.id]: nextStatus }));
    try {
      const { error } = await marketingAdmin
        .from('website_leads')
        .update({ status: nextStatus })
        .eq('id', row.id);
      if (error) {
        alert(error.message || 'Failed to update status');
        return;
      }
      await refetch();
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  };

  const renderCarLoanRows = () => (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PAN</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Income</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occupation</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50">
            <td className="px-4 py-4">
              <CustomerCell row={row} />
            </td>
            <td className="px-4 py-4 text-sm font-mono">{row.pan_number || '—'}</td>
            <td className="px-4 py-4 text-sm">{row.vehicle_registration || '—'}</td>
            <td className="px-4 py-4 text-sm">₹{Number(row.monthly_income || 0).toLocaleString('en-IN')}</td>
            <td className="px-4 py-4 text-sm">{row.occupation || '—'}</td>
            <td className="px-4 py-4"><StatusBadge value={row.status} /></td>
            <td className="px-4 py-4 text-sm text-gray-600">{fmtDate(row.created_at)}</td>
            <td className="px-4 py-4 text-right whitespace-nowrap">
              <EnquiryActionButtons
                row={row}
                closed={isSimpleEnquiryClosed(row)}
                updating={updating[row.id]}
                onComplete={() => handleEnquiryAction('car_loan', row, 'complete')}
                onCancel={() => handleEnquiryAction('car_loan', row, 'cancel')}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );

  const renderCarServiceRows = () => (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City / Slot</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pickup</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booked</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => {
          const meta = parseGarageNotes(row.notes);
          const city = row.booking_city || meta.city;
          const slot = row.pickup_slot_label || meta.pickup_slot_label;
          const paid = row.amount_paid ?? row.amount ?? meta.amount_paid;
          return (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="px-4 py-4">
                <CustomerCell row={row} />
              </td>
              <td className="px-4 py-4">
                <GarageVehicleCell row={row} />
              </td>
              <td className="px-4 py-4">
                <GarageServiceCell row={row} />
              </td>
              <td className="px-4 py-4 text-sm">
                <div>{city || '—'}</div>
                <div className="text-gray-500 text-xs">{slot || ''}</div>
              </td>
              <td className="px-4 py-4 text-sm">{fmtDate(row.booking_date)}</td>
              <td className="px-4 py-4 text-sm">
                <div className="font-medium text-gray-900">₹{Number(paid || 0).toLocaleString('en-IN')}</div>
                {row.coupon_code || meta.coupon_code ? (
                  <div className="text-xs text-green-700 mt-1">Coupon: {row.coupon_code || meta.coupon_code}</div>
                ) : null}
                {row.razorpay_payment_id || meta.razorpay_payment_id ? (
                  <div className="text-xs text-gray-500 mt-1 font-mono truncate max-w-[140px]" title={row.razorpay_payment_id || meta.razorpay_payment_id}>
                    Pay: {row.razorpay_payment_id || meta.razorpay_payment_id}
                  </div>
                ) : null}
              </td>
              <td className="px-4 py-4"><StatusBadge value={row.status} /></td>
              <td className="px-4 py-4 text-sm text-gray-600">{fmtDate(row.created_at)}</td>
              <td className="px-4 py-4 text-right whitespace-nowrap">
                <EnquiryActionButtons
                  row={row}
                  closed={isSimpleEnquiryClosed(row)}
                  updating={updating[row.id]}
                  onComplete={() => handleEnquiryAction('car_service', row, 'complete')}
                  onCancel={() => handleEnquiryAction('car_service', row, 'cancel')}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </>
  );

  const renderRsaRows = () => (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membership</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GPS / Location</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Complaint</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50">
            <td className="px-4 py-4">
              <CustomerCell row={row} />
            </td>
            <td className="px-4 py-4 text-sm">{row.service_type || '—'}</td>
            <td className="px-4 py-4">
              <RsaVehicleCell row={row} />
            </td>
            <td className="px-4 py-4">
              <RsaMembershipCell row={row} />
            </td>
            <td className="px-4 py-4">
              <RsaLocationCell row={row} />
            </td>
            <td className="px-4 py-4"><StatusBadge value={row.request_status} /></td>
            <td className="px-4 py-4"><StatusBadge value={row.complaint_status} /></td>
            <td className="px-4 py-4 text-sm text-gray-600">
              {fmtDate(row.complaint_registered_at || row.requested_at)}
            </td>
            <td className="px-4 py-4 text-right whitespace-nowrap">
              <EnquiryActionButtons
                row={row}
                closed={isRsaClosed(row)}
                updating={updating[row.id]}
                onComplete={() => handleEnquiryAction('rsa', row, 'complete')}
                onCancel={() => handleEnquiryAction('rsa', row, 'cancel')}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );

  const WEBSITE_STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'converted', label: 'Converted' },
    { value: 'closed', label: 'Closed' },
  ];

  const renderWebsiteRows = () => (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City / Service</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Update</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50">
            <td className="px-4 py-4">
              <div className="font-semibold text-gray-900">{row.name || '—'}</div>
              <a href={`tel:${row.phone}`} className="text-sm font-medium text-primary-600 hover:underline">
                {row.phone}
              </a>
            </td>
            <td className="px-4 py-4 max-w-md">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{row.message || '—'}</p>
            </td>
            <td className="px-4 py-4 text-sm text-gray-600">{row.source || 'website'}</td>
            <td className="px-4 py-4 text-sm text-gray-600">
              {[row.city, row.service].filter(Boolean).join(' · ') || '—'}
            </td>
            <td className="px-4 py-4">
              <StatusBadge value={row.status || 'new'} />
            </td>
            <td className="px-4 py-4 text-sm text-gray-600">{fmtDate(row.created_at)}</td>
            <td className="px-4 py-4 text-right">
              <select
                value={row.status || 'new'}
                disabled={!!updating[row.id]}
                onChange={(e) => handleWebsiteStatus(row, e.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
              >
                {WEBSITE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );

  const searchPlaceholder =
    activeTab === 'website'
      ? 'Search name, phone, message...'
      : 'Search name, phone, vehicle, card no, PAN...';

  const emptyHint =
    activeTab === 'website'
      ? 'Contact form messages from app.roadserve.in will appear here.'
      : 'New app submissions will appear here.';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">App Enquiries</h1>
          <p className="text-gray-600 mt-1">Customer enquiries from the mobile app and website contact form</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          const count = tabCounts[tab.id];
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                active
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon size={16} />
              {tab.label}
              {active && count != null ? (
                <span className={`px-2 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : 'bg-gray-100'}`}>
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 md:col-span-2">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Search
            </button>
            {search ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Car size={20} className="text-primary-600" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total in {TABS.find((t) => t.id === activeTab)?.label}</p>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4" />
              <p className="text-gray-600">Loading enquiries...</p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <p className="text-lg font-medium text-gray-700">No enquiries found</p>
            <p className="text-sm mt-1">
              {search ? 'Try a different search term.' : emptyHint}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full ${activeTab === 'website' ? 'min-w-[900px]' : 'min-w-[1400px]'}`}>
              {activeTab === 'car_loan' && renderCarLoanRows()}
              {activeTab === 'car_service' && renderCarServiceRows()}
              {activeTab === 'rsa' && renderRsaRows()}
              {activeTab === 'website' && renderWebsiteRows()}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppEnquiries;
